import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Plus, Trash2, BookOpen, Clock,
    Play, ChevronRight, Search, MoreVertical, Edit, Eye
} from 'lucide-react'
import { useToast } from '../hooks/useToast'
import { supabase } from '../services/supabase'
import { useAuth } from '../contexts/AuthContext'
import Button from '../components/Button'
import ModalCurso from '../components/ModalCurso'
import { ToastContainer } from '../components/Toast'

interface Curso {
    id: string
    titulo: string
    descricao: string | null
    imagem_url: string | null
    duracao_estimada: number
    nivel: string
    status: string
    ordem_exibicao: number
    created_at: string
    updated_at: string
    total_modulos: number
    total_aulas: number
    aulas_assistidas: number
}

export default function AdminCursos() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { toasts, showSuccess, showError } = useToast()
    const [cursos, setCursos] = useState<Curso[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('todos')
    const [nivelFilter, setNivelFilter] = useState('todos')
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingCurso, setEditingCurso] = useState<Curso | null>(null)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [cursoToDelete, setCursoToDelete] = useState<Curso | null>(null)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)

    useEffect(() => {
        loadCursos()
    }, [])

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            const dropdown = target.closest('.dropdown-container')

            if (activeDropdown && !dropdown) {
                setActiveDropdown(null)
            }
        }

        if (activeDropdown) {
            document.addEventListener('click', handleClickOutside)
        }

        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [activeDropdown])

    const loadCursos = async () => {
        try {
            setLoading(true)

            // Buscar todos os cursos
            const { data: cursosData, error: cursosError } = await supabase
                .from('cursos')
                .select('*')
                .order('ordem_exibicao')

            if (cursosError) throw cursosError

            // Para cada curso, buscar contagem de módulos e aulas
            const cursosComContadores = await Promise.all(
                (cursosData || []).map(async (curso) => {
                    // Contar módulos do curso
                    const { count: modulosCount } = await supabase
                        .from('modulos')
                        .select('*', { count: 'exact', head: true })
                        .eq('curso_id', curso.id)

                    // Contar aulas do curso (através dos módulos)
                    // Primeiro buscar todos os módulos do curso
                    const { data: modulosData } = await supabase
                        .from('modulos')
                        .select('id')
                        .eq('curso_id', curso.id)

                    // Se não há módulos, não há aulas
                    if (!modulosData || modulosData.length === 0) {
                        return {
                            ...curso,
                            total_modulos: modulosCount || 0,
                            total_aulas: 0,
                            aulas_assistidas: 0,
                            duracao_estimada: 0 // Sem aulas, duração é 0
                        }
                    }

                    // Buscar aulas dos módulos do curso
                    const { count: aulasCount, error: aulasError } = await supabase
                        .from('aulas')
                        .select('*', { count: 'exact', head: true })
                        .in('modulo_id', modulosData.map(m => m.id))

                    // Verificar se há erro na consulta de aulas
                    if (aulasError) {
                        console.error('Erro ao contar aulas do curso:', curso.titulo, aulasError)
                    }

                    // Calcular duração total somando todas as aulas do curso
                    const { data: aulasData, error: aulasDuracaoError } = await supabase
                        .from('aulas')
                        .select('duracao')
                        .in('modulo_id', modulosData.map(m => m.id))
                        .eq('status', 'ativo')

                    let duracaoTotal = curso.duracao_estimada // Usar valor do banco como fallback
                    if (!aulasDuracaoError && aulasData) {
                        // Somar todas as durações das aulas (em minutos)
                        duracaoTotal = aulasData.reduce((total, aula) => {
                            return total + (aula.duracao || 0)
                        }, 0)
                    }

                    // Contar aulas assistidas pelo usuário atual
                    const { count: aulasAssistidasCount } = await supabase
                        .from('progresso_aulas')
                        .select(`
                            *,
                            aulas!inner(
                                modulo_id,
                                modulos!inner(curso_id)
                            )
                        `, { count: 'exact', head: true })
                        .eq('user_id', user?.id)
                        .eq('assistida', true)
                        .eq('aulas.modulos.curso_id', curso.id)

                    return {
                        ...curso,
                        total_modulos: modulosCount || 0,
                        total_aulas: aulasCount || 0,
                        aulas_assistidas: aulasAssistidasCount || 0,
                        duracao_estimada: duracaoTotal // Duração calculada da soma das aulas
                    }
                })
            )

            setCursos(cursosComContadores)
        } catch (err) {
            console.error('Erro ao carregar cursos:', err)
            showError('Erro ao carregar lista de cursos. Verifique sua conexão e tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!cursoToDelete) return

        try {
            const { error } = await supabase
                .from('cursos')
                .delete()
                .eq('id', cursoToDelete.id)

            if (error) throw error

            showSuccess(`Curso "${cursoToDelete.titulo}" excluído com sucesso!`)
            loadCursos()
        } catch (err) {
            console.error('Erro ao excluir curso:', err)
            showError(`Erro ao excluir curso "${cursoToDelete.titulo}". Tente novamente.`)
        } finally {
            setIsDeleteModalOpen(false)
            setCursoToDelete(null)
        }
    }

    const openDeleteModal = (curso: Curso) => {
        setCursoToDelete(curso)
        setIsDeleteModalOpen(true)
        setActiveDropdown(null)
    }

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false)
        setCursoToDelete(null)
    }

    const handleEditCurso = (curso: Curso) => {
        setEditingCurso(curso)
        setIsModalOpen(true)
        setActiveDropdown(null)
    }

    const toggleDropdown = (cursoId: string, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation()
        }
        setActiveDropdown(activeDropdown === cursoId ? null : cursoId)
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ativo':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            case 'inativo':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            case 'em_breve':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }

    const getNivelColor = (nivel: string) => {
        switch (nivel) {
            case 'iniciante':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
            case 'intermediario':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            case 'avancado':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }

    const formatarDuracao = (minutos: number) => {
        const horas = Math.floor(minutos / 60)
        const mins = minutos % 60
        if (horas > 0) {
            return `${horas}h ${mins}min`
        }
        return `${mins}min`
    }

    const filteredCursos = cursos.filter(curso => {
        const matchesSearch = curso.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (curso.descricao && curso.descricao.toLowerCase().includes(searchTerm.toLowerCase()))

        const matchesStatus = statusFilter === 'todos' || curso.status === statusFilter
        const matchesNivel = nivelFilter === 'todos' || curso.nivel === nivelFilter

        return matchesSearch && matchesStatus && matchesNivel
    })

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                                Administração de Cursos
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400">
                                Gerencie seus cursos, módulos e aulas
                            </p>
                        </div>
                        <Button
                            onClick={() => {
                                setEditingCurso(null)
                                setIsModalOpen(true)
                            }}
                            variant="btn_add"
                            className="flex items-center gap-2"
                        >
                            <Plus size={20} />
                            Novo Curso
                        </Button>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Busca */}
                        <div className="md:col-span-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
                                <input
                                    type="text"
                                    placeholder="Buscar cursos..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 w-full border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                />
                            </div>
                        </div>

                        {/* Filtro Status */}
                        <div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todos os Status</option>
                                <option value="ativo">Ativo</option>
                                <option value="inativo">Inativo</option>
                                <option value="em_breve">Em Breve</option>
                            </select>
                        </div>

                        {/* Filtro Nível */}
                        <div>
                            <select
                                value={nivelFilter}
                                onChange={(e) => setNivelFilter(e.target.value)}
                                className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="todos">Todos os Níveis</option>
                                <option value="iniciante">Iniciante</option>
                                <option value="intermediario">Intermediário</option>
                                <option value="avancado">Avançado</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Grid de Cursos */}
                {filteredCursos.length === 0 ? (
                    <div className="text-center py-16">
                        <BookOpen className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhum curso encontrado
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            {searchTerm || statusFilter !== 'todos' || nivelFilter !== 'todos'
                                ? 'Tente ajustar os filtros de busca.'
                                : 'Crie seu primeiro curso para começar.'
                            }
                        </p>
                        {!searchTerm && statusFilter === 'todos' && nivelFilter === 'todos' && (
                            <Button
                                onClick={() => {
                                    setEditingCurso(null)
                                    setIsModalOpen(true)
                                }}
                                variant="btn_add"
                                className="px-8 py-3"
                            >
                                Criar Primeiro Curso
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCursos.map((curso) => (
                            <div
                                key={curso.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
                            >
                                {/* Imagem do Curso */}
                                <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                                    {curso.imagem_url ? (
                                        <img
                                            src={curso.imagem_url}
                                            alt={curso.titulo}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <BookOpen className="h-16 w-16 text-white opacity-80" />
                                        </div>
                                    )}

                                    {/* Badges */}
                                    <div className="absolute top-3 left-3 flex gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(curso.status)}`}>
                                            {curso.status}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNivelColor(curso.nivel)}`}>
                                            {curso.nivel}
                                        </span>
                                    </div>

                                    {/* Menu de Ações */}
                                    <div className="absolute top-3 right-3">
                                        <div className="relative dropdown-container">
                                            <button
                                                className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors duration-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 rounded-lg"
                                                onClick={(e: React.MouseEvent) => toggleDropdown(curso.id, e)}
                                            >
                                                <MoreVertical className="h-4 w-4" />
                                            </button>

                                            {/* Dropdown Menu */}
                                            {activeDropdown === curso.id && (
                                                <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                                                    <div className="py-1">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleEditCurso(curso)
                                                            }}
                                                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                            Editar Curso
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                navigate(`/cursos/${curso.id}`)
                                                            }}
                                                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                            Visualizar
                                                        </button>
                                                        <hr className="my-1 border-gray-200 dark:border-gray-700" />
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                openDeleteModal(curso)
                                                            }}
                                                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                            Excluir
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Conteúdo do Card */}
                                <div className="p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                                        {curso.titulo}
                                    </h3>

                                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                                        {curso.descricao || 'Descrição não disponível'}
                                    </p>

                                    {/* Informações do Curso */}
                                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                                        <div className="flex items-center gap-1">
                                            <Clock className="h-4 w-4" />
                                            <span>{formatarDuracao(curso.duracao_estimada)}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <BookOpen className="h-4 w-4" />
                                            <span>{curso.total_modulos} módulos</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Play className="h-4 w-4" />
                                            <span>{curso.aulas_assistidas}/{curso.total_aulas} aulas</span>
                                        </div>
                                    </div>

                                    {/* Botões de Ação */}
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="btn_secondary"
                                            size="sm"
                                            onClick={() => navigate(`/admin/cursos/${curso.id}/modulos`)}
                                            className="flex items-center gap-2 flex-1"
                                        >
                                            <BookOpen className="h-4 w-4" />
                                            Gerenciar Módulos
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal de Exclusão */}
                {isDeleteModalOpen && cursoToDelete && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                        <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                    </div>
                                </div>
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                                        Excluir Curso
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                        Tem certeza que deseja excluir o curso <strong>"{cursoToDelete.titulo}"</strong>?
                                        Esta ação não pode ser desfeita e excluirá todos os módulos e aulas associados.
                                    </p>
                                    <div className="flex gap-3 justify-center">
                                        <Button
                                            variant="btn_secondary"
                                            onClick={closeDeleteModal}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="btn_secondary"
                                            onClick={handleDelete}
                                            className="bg-red-600 hover:bg-red-700 text-white"
                                        >
                                            Excluir
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de Curso */}
                <ModalCurso
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false)
                        setEditingCurso(null)
                    }}
                    onSuccess={() => {
                        loadCursos()
                        setIsModalOpen(false)
                        setEditingCurso(null)
                    }}
                    curso={editingCurso}
                    onShowToast={(type, title, message) => {
                        if (type === 'success') {
                            showSuccess(title, message)
                        } else if (type === 'error') {
                            showError(title, message)
                        }
                    }}
                />

                {/* Toast Container */}
                <ToastContainer toasts={toasts} />
            </div>
        </div>
    )
}
